/**
 * Word-Zeilenmodell für den Fokusmodus.
 *
 * Bildet nach, wie MS Word den von `docx_report_service._html_to_docx_elements`
 * erzeugten Report setzt: dieselben Absatztypen, Schriften, Zeilenhöhen,
 * Abstände und Umbruchregeln (Blocksatz mit bis zu 20 % gestauchten
 * Wortabständen, Witwen-/Waisenkontrolle). Die Geometrie stammt aus
 * `wordPageGeometry.json` (aus den DOCX-Templates abgeleitet, Backend-Test
 * `test_word_page_geometry.py`), die Dickten aus Canvas-Messung mit
 * Arial Narrow bzw. dem metrik-kompatiblen Liberation Sans Narrow.
 */

import geometryJson from './wordPageGeometry.json';

export type WordLang = 'de' | 'en';
export type WordFontKind = 'narrow' | 'code' | 'mono';

type GeometryJson = typeof geometryJson;
export type WordGeometry = GeometryJson['en'];

export const WORD_GEOMETRY: Record<WordLang, WordGeometry> = geometryJson as Record<WordLang, WordGeometry>;

export const wordGeometry = (lang: WordLang = 'de'): WordGeometry => WORD_GEOMETRY[lang] ?? WORD_GEOMETRY.de;

export const PT_PER_PX = 0.75;
export const ptToPx = (pt: number): number => (pt * 96) / 72;
export const pxToPt = (px: number): number => px * PT_PER_PX;

/** CSS-Familien aus `wordFonts.css`: lokal Arial Narrow / Consolas / Courier New, sonst Liberation. */
export const WORD_FONT_FAMILY: Record<WordFontKind, string> = {
  narrow: '"STARK Word Narrow", "Arial Narrow", "Liberation Sans Narrow", Arial, sans-serif',
  code: '"STARK Word Code", Consolas, "Liberation Mono", monospace',
  mono: '"STARK Word Mono", "Courier New", "Liberation Mono", monospace',
};

export interface WordRun {
  text: string;
  font: WordFontKind;
  sizePt: number;
  bold: boolean;
  italic: boolean;
  /** DOM-Textknoten + Offset des ersten Zeichens (für Marker-Positionen). */
  node?: Text;
  nodeOffset?: number;
}

export interface WordParagraph {
  kind: 'paragraph';
  runs: WordRun[];
  /** Zeilenhöhe eines leeren Absatzes (Absatzmarke) in pt. */
  markLinePt: number;
  /** Faktor `w:spacing/@w:line` (auto): Console 1.15, sonst 1. */
  lineRule: number;
  afterPt: number;
  widthPt: number;
  justified: boolean;
  widowControl: boolean;
  keepNext: boolean;
  /** Nächster Block beginnt in Word oben auf einer neuen Seite (`w:br type="page"`). */
  pageBreakBefore?: boolean;
  pageBreakAfter?: boolean;
  dom?: Element;
  label: string;
}

export interface WordBox {
  kind: 'box';
  heightPt: number;
  afterPt: number;
  /** Word darf innerhalb umbrechen (z. B. Bild höher als eine Seite → wird geschnitten). */
  splittable: boolean;
  pageBreakBefore?: boolean;
  pageBreakAfter?: boolean;
  dom?: Element;
  label: string;
}

export type WordBlock = WordParagraph | WordBox;

export interface WordLine {
  blockIndex: number;
  block: WordBlock;
  lineIndex: number;
  lineCount: number;
  heightPt: number;
  /** Absatzabstand nach dieser Zeile (nur letzte Zeile eines Blocks). */
  afterPt: number;
  /** Erstes Zeichen der Zeile (Absätze): Run-Index + Offset im Run. */
  startRun: number;
  startOffset: number;
  text: string;
  widowControl: boolean;
  keepNext: boolean;
  /** Natürliche Breite und nötige Stauchung (Blocksatz), nur Diagnose. */
  naturalWidthPt: number;
  compressionPt: number;
}

export interface TextMeasurer {
  /** Dickte eines Textstücks in pt (ohne Kerning, lineare Advances). */
  widthPt(text: string, run: WordRun): number;
}

const NARROW_LINE_EM = wordGeometry('de').body.lineEm;

/** Zeilenhöhe „einfach“ eines Laufs in pt. */
export function runLinePt(run: WordRun, geometry: WordGeometry = wordGeometry()): number {
  if (run.font === 'code') return (geometry.inlineCode.linePt / geometry.inlineCode.sizePt) * run.sizePt;
  if (run.font === 'mono') {
    return (geometry.console.linePt / geometry.console.sizePt / 1.15) * run.sizePt;
  }
  return Math.round(run.sizePt * NARROW_LINE_EM * 100) / 100;
}

export const bodyRun = (text: string, geometry: WordGeometry = wordGeometry(), extra: Partial<WordRun> = {}): WordRun => ({
  text,
  font: 'narrow',
  sizePt: geometry.body.sizePt,
  bold: false,
  italic: false,
  ...extra,
});

/* ────────────────────────────── Messen ────────────────────────────── */

/** Monospace-Dickten sind arithmetisch exakt; nur Arial Narrow braucht die Canvas. */
export function monoWidthPt(text: string, run: WordRun, geometry: WordGeometry = wordGeometry()): number {
  const em = run.font === 'code' ? geometry.inlineCode.advanceEm : geometry.console.advanceEm;
  return Array.from(text).length * em * run.sizePt;
}

export function createCanvasMeasurer(geometry: WordGeometry = wordGeometry()): TextMeasurer | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d') as (CanvasRenderingContext2D & { fontKerning?: string; textRendering?: string }) | null;
  if (!ctx) return null;
  if ('fontKerning' in ctx) ctx.fontKerning = 'none';
  if ('textRendering' in ctx) ctx.textRendering = 'geometricPrecision';
  const cache = new Map<string, number>();
  return {
    widthPt(text, run) {
      if (!text) return 0;
      if (run.font !== 'narrow') return monoWidthPt(text, run, geometry);
      const key = `${run.bold ? 'b' : ''}${run.italic ? 'i' : ''}${run.sizePt}|${text}`;
      const hit = cache.get(key);
      if (hit != null) return hit;
      ctx.font = `${run.italic ? 'italic ' : ''}${run.bold ? 700 : 400} ${ptToPx(run.sizePt)}px ${WORD_FONT_FAMILY.narrow}`;
      const w = pxToPt(ctx.measureText(text).width);
      cache.set(key, w);
      return w;
    },
  };
}

/** Messer aus einer Advance-Tabelle (Font-Units) — für Tests ohne Canvas. */
export function createTableMeasurer(
  faces: { unitsPerEm: number; faces: Record<string, Record<string, number>> },
  geometry: WordGeometry = wordGeometry(),
): TextMeasurer {
  const pick = (run: WordRun) => {
    const name = run.bold && run.italic ? 'boldItalic' : run.bold ? 'bold' : run.italic ? 'italic' : 'regular';
    return faces.faces[name] ?? faces.faces.regular;
  };
  return {
    widthPt(text, run) {
      if (run.font !== 'narrow') return monoWidthPt(text, run, geometry);
      const table = pick(run);
      const fallback = table['\ufffd'] ?? table['?'] ?? 1024;
      let units = 0;
      for (const ch of Array.from(text)) units += table[ch] ?? fallback;
      return (units / faces.unitsPerEm) * run.sizePt;
    },
  };
}

/* ─────────────────────────── Zeilenumbruch ─────────────────────────── */

interface Token {
  kind: 'word' | 'space' | 'break';
  text: string;
  runIndex: number;
  offset: number;
  run: WordRun;
  /** Word erlaubt Umbruch nach Bindestrich/Schrägstrich innerhalb eines Wortes. */
  breakAfter: boolean;
}

const BREAK_AFTER_CHARS = new Set(['-', '\u2013', '\u2014', '/', '\u2011']);

function tokenize(runs: WordRun[]): Token[] {
  const tokens: Token[] = [];
  runs.forEach((run, runIndex) => {
    let word = '';
    let wordStart = 0;
    const flush = (end: number) => {
      if (word) tokens.push({ kind: 'word', text: word, runIndex, offset: wordStart, run, breakAfter: false });
      word = '';
      wordStart = end;
    };
    const chars = Array.from(run.text);
    let offset = 0;
    for (const ch of chars) {
      if (ch === '\n') {
        flush(offset);
        tokens.push({ kind: 'break', text: '\n', runIndex, offset, run, breakAfter: false });
      } else if (ch === ' ' || ch === '\t') {
        flush(offset);
        tokens.push({ kind: 'space', text: ' ', runIndex, offset, run, breakAfter: false });
      } else {
        if (!word) wordStart = offset;
        word += ch;
        if (BREAK_AFTER_CHARS.has(ch)) {
          const t: Token = { kind: 'word', text: word, runIndex, offset: wordStart, run, breakAfter: true };
          tokens.push(t);
          word = '';
        }
      }
      offset += ch.length;
    }
    flush(offset);
  });
  // Ein Wort, das an einer Run-Grenze weitergeht (z. B. fett/normal), bleibt ein Wort:
  // die Tokens werden im Umbruch als Kette ohne Trenn-Chance behandelt.
  return tokens;
}

interface OpenLine {
  tokens: Token[];
  natural: number;
  spaces: number;
  startRun: number;
  startOffset: number;
  pendingSpaceWidth: number;
  pendingSpaces: Token[];
}

/**
 * Bricht einen Absatz so um wie Word: gierig an Leerzeichen (und nach -, /),
 * ohne Silbentrennung; im Blocksatz passt eine Zeile noch, wenn die
 * Wortabstände um höchstens `maxSpaceCompression` gestaucht werden müssen.
 * Zu lange Einzelwörter werden zeichenweise geteilt (Notumbruch).
 */
export function breakParagraphIntoLines(
  paragraph: WordParagraph,
  measurer: TextMeasurer,
  geometry: WordGeometry = wordGeometry(),
): WordLine[] {
  const compress = paragraph.justified ? geometry.body.maxSpaceCompression : 0;
  const width = paragraph.widthPt;
  // Word setzt in Twips und rundet Dickten je Glyphe; am Report 23 liegt die
  // Risk-Zelle 0.42 pt über dem Modell und passt in Word trotzdem → 10 Twips Toleranz.
  const eps = 0.5;
  const lines: WordLine[] = [];
  const tokens = tokenize(paragraph.runs);

  const widthOf = (t: Token) => measurer.widthPt(t.text, t.run);

  const newLine = (startRun: number, startOffset: number): OpenLine => ({
    tokens: [],
    natural: 0,
    spaces: 0,
    startRun,
    startOffset,
    pendingSpaceWidth: 0,
    pendingSpaces: [],
  });

  const finish = (line: OpenLine, forceEmpty = false) => {
    if (!line.tokens.length && !forceEmpty) return;
    let linePt = 0;
    for (const t of line.tokens) linePt = Math.max(linePt, runLinePt(t.run, geometry));
    if (!line.tokens.length) linePt = paragraph.markLinePt;
    linePt *= paragraph.lineRule;
    const text = line.tokens.map((t) => t.text).join('');
    lines.push({
      blockIndex: -1,
      block: paragraph,
      lineIndex: lines.length,
      lineCount: 0,
      heightPt: Math.round(linePt * 100) / 100,
      afterPt: 0,
      startRun: line.startRun,
      startOffset: line.startOffset,
      text,
      widowControl: paragraph.widowControl,
      keepNext: paragraph.keepNext,
      naturalWidthPt: line.natural,
      compressionPt: Math.max(0, line.natural - width),
    });
  };

  const fits = (natural: number, spaces: number) => natural - compress * spaces <= width + eps;

  let line = newLine(0, 0);
  let i = 0;
  let endsWithBreak = false;
  while (i < tokens.length) {
    const tok = tokens[i];
    endsWithBreak = false;
    if (tok.kind === 'break') {
      // Manueller Zeilenumbruch (<br> → w:br): Zeile schließen, auch wenn leer.
      finish(line, true);
      const next = tokens[i + 1];
      line = newLine(next ? next.runIndex : tok.runIndex, next ? next.offset : tok.offset + 1);
      i += 1;
      endsWithBreak = true;
      continue;
    }
    if (tok.kind === 'space') {
      // Leerzeichen am Zeilenanfang setzt Word auch (kein Kollabieren), am Zeilenende hängen sie über.
      line.pendingSpaces.push(tok);
      line.pendingSpaceWidth += widthOf(tok);
      i += 1;
      continue;
    }
    // Wortkette: Tokens ohne Trenn-Chance dazwischen (Run-Wechsel mitten im Wort).
    let j = i;
    const chain: Token[] = [];
    let chainWidth = 0;
    while (j < tokens.length && tokens[j].kind === 'word') {
      chain.push(tokens[j]);
      chainWidth += widthOf(tokens[j]);
      if (tokens[j].breakAfter) {
        j += 1;
        break;
      }
      j += 1;
    }
    const candidateNatural = line.natural + line.pendingSpaceWidth + chainWidth;
    const candidateSpaces = line.spaces + line.pendingSpaceWidth;
    if (fits(candidateNatural, candidateSpaces)) {
      line.tokens.push(...line.pendingSpaces, ...chain);
      line.natural = candidateNatural;
      line.spaces = candidateSpaces;
      line.pendingSpaces = [];
      line.pendingSpaceWidth = 0;
      i = j;
      continue;
    }
    if (line.tokens.length) {
      // Zeile voll → Wort auf die nächste Zeile (überhängende Leerzeichen entfallen).
      finish(line);
      line = newLine(chain[0].runIndex, chain[0].offset);
      continue;
    }
    // Wort allein breiter als die Zeile: zeichenweise teilen.
    const first = chain[0];
    const chars = Array.from(first.text);
    let take = 0;
    let acc = 0;
    for (const ch of chars) {
      const w = measurer.widthPt(ch, first.run);
      if (take > 0 && acc + w > width + eps) break;
      acc += w;
      take += 1;
    }
    const head = chars.slice(0, take).join('');
    const tail = chars.slice(take).join('');
    line.tokens.push({ ...first, text: head, breakAfter: false });
    line.natural = acc;
    finish(line);
    const rest: Token[] = tail ? [{ ...first, text: tail, offset: first.offset + head.length }] : [];
    tokens.splice(i, 1, ...rest);
    line = newLine(rest.length ? rest[0].runIndex : first.runIndex, rest.length ? rest[0].offset : first.offset + head.length);
  }
  // Nach einem abschließenden <br> steht die Absatzmarke auf einer eigenen (leeren) Zeile.
  finish(line, lines.length === 0 || endsWithBreak);

  lines.forEach((l) => {
    l.lineCount = lines.length;
  });
  if (lines.length) lines[lines.length - 1].afterPt = paragraph.afterPt;
  return lines;
}

/** Alle Blöcke zu Zeilen; Boxen (Bilder, Tabellenzeilen, Platzhalter) sind eine Zeile. */
export function layoutWordBlocks(blocks: WordBlock[], measurer: TextMeasurer, geometry: WordGeometry = wordGeometry()): WordLine[] {
  const out: WordLine[] = [];
  blocks.forEach((block, blockIndex) => {
    if (block.kind === 'box') {
      out.push({
        blockIndex,
        block,
        lineIndex: 0,
        lineCount: 1,
        heightPt: block.heightPt,
        afterPt: block.afterPt,
        startRun: 0,
        startOffset: 0,
        text: block.label,
        widowControl: false,
        keepNext: false,
        naturalWidthPt: 0,
        compressionPt: 0,
      });
      return;
    }
    for (const line of breakParagraphIntoLines(block, measurer, geometry)) {
      out.push({ ...line, blockIndex });
    }
  });
  return out;
}

/** Summe der Zeilenhöhen inkl. Absatzabständen (pt). */
export function flowHeightPt(lines: WordLine[]): number {
  return lines.reduce((sum, l) => sum + l.heightPt + l.afterPt, 0);
}

/* ──────────────────────────── Paginierung ──────────────────────────── */

export interface WordPageBreak {
  /** Index der ersten Zeile auf der neuen Seite. */
  lineIndex: number;
  /** Oberkante dieser Zeile im Fluss (pt, Absatzabstände mitgezählt). */
  flowTopPt: number;
  /** Warum dieser Umbruch hier liegt (Diagnose/Tooltip). */
  reason: 'overflow' | 'widow' | 'orphan' | 'keep-next' | 'tall-box' | 'manual';
}

export interface WordPagination {
  breaks: WordPageBreak[];
  pageCount: number;
  /** Zeilen-Oberkanten im Fluss (pt). */
  lineTopsPt: number[];
  totalHeightPt: number;
}

/**
 * Word-Seitenumbruch über einen Zeilenfluss: die erste Zeile, die nicht mehr
 * passt, rutscht auf die nächste Seite. Witwen-/Waisenkontrolle (Standard in
 * Word an): nie nur eine Zeile eines Absatzes am Seitenende oder -anfang;
 * `keepNext` zieht Überschriften mit. Absatzabstand nach der letzten Zeile
 * einer Seite muss nicht mehr passen.
 */
export function paginateWordLines(lines: WordLine[], pageHeightPt: number): WordPagination {
  const tops: number[] = [];
  let y = 0;
  for (const l of lines) {
    tops.push(y);
    y += l.heightPt + l.afterPt;
  }
  const total = y;
  const breaks: WordPageBreak[] = [];
  if (pageHeightPt <= 0 || !lines.length) return { breaks, pageCount: 1, lineTopsPt: tops, totalHeightPt: total };

  const bottom = (i: number) => tops[i] + lines[i].heightPt;
  const eps = 0.05;
  let pageStart = 0;
  let pageEnd = pageHeightPt;
  let i = 0;
  let guard = 0;

  const forceBreakAt = (j: number, reason: WordPageBreak['reason']): boolean => {
    if (j <= 0 || j >= lines.length) return false;
    if (tops[j] <= pageStart + eps) return false;
    if (breaks.length && breaks[breaks.length - 1].lineIndex === j) return false;
    breaks.push({ lineIndex: j, flowTopPt: tops[j], reason });
    pageStart = tops[j];
    pageEnd = pageStart + pageHeightPt;
    return true;
  };

  while (i < lines.length && guard++ < 5000) {
    const cur = lines[i];
    if (cur.lineIndex === 0 && cur.block.pageBreakBefore) {
      forceBreakAt(i, 'manual');
    }
    if (bottom(i) <= pageEnd + eps) {
      const accepted = lines[i];
      i += 1;
      if (
        accepted.block.pageBreakAfter &&
        accepted.lineIndex === accepted.lineCount - 1 &&
        i < lines.length
      ) {
        forceBreakAt(i, 'manual');
      }
      continue;
    }
    // Zeile i passt nicht mehr.
    const line = lines[i];
    if (tops[i] <= pageStart + eps) {
      // Erste Zeile der Seite ist höher als die Seite (Bild): Word schneidet.
      breaks.push({ lineIndex: i, flowTopPt: pageEnd, reason: 'tall-box' });
      pageStart = pageEnd;
      pageEnd = pageStart + pageHeightPt;
      continue;
    }
    let j = i;
    let reason: WordPageBreak['reason'] = 'overflow';
    const blk = line.block;
    if (blk.kind === 'paragraph' && line.widowControl && line.lineCount >= 2) {
      const firstIdx = i - line.lineIndex;
      if (line.lineIndex === line.lineCount - 1) {
        // Nur die letzte Zeile würde wandern → zwei Zeilen mitnehmen.
        j = i - 1;
        reason = 'widow';
      }
      if (j - firstIdx === 1) {
        // Nur eine Zeile bliebe zurück → ganzen Absatz verschieben.
        j = firstIdx;
        reason = reason === 'widow' ? 'widow' : 'orphan';
      }
    }
    // keepNext: Überschrift(en) direkt davor mitnehmen, wenn ihr Absatz komplett oben steht.
    while (j > 0) {
      const prev = lines[j - 1];
      const cur = lines[j];
      const curIsBlockStart = cur.lineIndex === 0;
      if (curIsBlockStart && prev.keepNext && prev.lineIndex === prev.lineCount - 1) {
        j -= prev.lineCount;
        reason = 'keep-next';
        continue;
      }
      break;
    }
    if (j <= 0 || tops[j] <= pageStart + eps) {
      // Würde die Seite leeren → Regel aufgeben, harter Umbruch vor Zeile i.
      j = i;
      reason = 'overflow';
    }
    breaks.push({ lineIndex: j, flowTopPt: tops[j], reason });
    pageStart = tops[j];
    pageEnd = pageStart + pageHeightPt;
    i = j;
    // Schutz: wenn die Zeile j selbst nicht passt, greift oben der tall-box-Zweig.
    if (bottom(j) <= pageEnd + eps) i = j + 1;
  }

  return { breaks, pageCount: breaks.length + 1, lineTopsPt: tops, totalHeightPt: total };
}

/* ───────────────────────── DOM → Word-Blöcke ───────────────────────── */

export interface CollectOptions {
  geometry?: WordGeometry;
  /** Verfügbare Breite der Grundebene (pt); Standard: Satzspiegel. */
  widthPt?: number;
  /** Nur bei gerendertem Editor-DOM: ProseMirror-Hilfs-<br> ignorieren. */
  editorDom?: boolean;
  /** Tabellenzeilen werden schon beim Sammeln umbrochen (Zeilenhöhe der Box). */
  measurerForTables?: TextMeasurer;
}

const isElement = (n: Node): n is Element => n.nodeType === 1;
const isText = (n: Node): n is Text => n.nodeType === 3;
/**
 * Bild im HTML (<img>) oder im Editor-DOM: TipTap rendert die NodeView als
 * <span class="react-renderer node-image"><span class="tiptap-image-node">…<img></span></span>.
 */
/** ProseMirror-Hilfsknoten (Cursor-Separator, trailing <br>) sind kein Inhalt. */
const isPmArtifact = (n: Node): boolean =>
  isElement(n) && (n.classList.contains('ProseMirror-separator') || n.classList.contains('ProseMirror-trailingBreak'));
const isImageNode = (n: Node): boolean => {
  if (!isElement(n) || isPmArtifact(n)) return false;
  if (n.tagName === 'IMG' || n.classList.contains('tiptap-image-node')) return true;
  if (n.classList.contains('node-image')) return true;
  return !(n.textContent || '').trim() && n.querySelector('img, .tiptap-image-node') != null;
};
const BLOCK_TAGS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'PRE', 'TABLE', 'BLOCKQUOTE', 'HR']);

function isPageBreakElement(el: Element): boolean {
  if (el.hasAttribute('data-page-break')) return true;
  if (el.getAttribute('data-type') === 'pageBreak') return true;
  return el.classList.contains('stark-page-break') || el.classList.contains('node-pageBreak');
}

function hasPageBreakBeforeAttr(el: Element): boolean {
  if (el.getAttribute('data-page-break-before') === 'true') return true;
  return /page-break-before\s*:\s*always/i.test(el.getAttribute('style') || '');
}

function readImageHeightPt(el: Element, geometry: WordGeometry): number {
  const attr = el.getAttribute('data-word-height-pt') ?? el.querySelector?.('[data-word-height-pt]')?.getAttribute('data-word-height-pt');
  if (attr && Number.isFinite(parseFloat(attr))) return parseFloat(attr);
  const img = el.tagName === 'IMG' ? (el as HTMLImageElement) : (el.querySelector('img') as HTMLImageElement | null);
  const sizeAttr = el.getAttribute('data-word-size') ?? img?.getAttribute('data-word-size') ?? 'half';
  const fraction = sizeAttr === 'full' ? 1 : sizeAttr === 'large' ? 2 / 3 : sizeAttr === 'small' ? 1 / 3 : 1 / 2;
  const widthPt = geometry.pagePt.contentWidth * fraction;
  const nw = img?.naturalWidth || parseFloat(img?.getAttribute('width') || '0');
  const nh = img?.naturalHeight || parseFloat(img?.getAttribute('height') || '0');
  if (nw > 0 && nh > 0) return widthPt * (nh / nw);
  return 0;
}

/**
 * Spiegelt `_html_to_docx_elements` + `_add_runs_from_inline`: welche
 * HTML-Knoten welche Word-Absätze/Läufe ergeben.
 */
export function collectWordBlocks(root: ParentNode, options: CollectOptions = {}): WordBlock[] {
  const geometry = options.geometry ?? wordGeometry();
  const baseWidth = options.widthPt ?? geometry.pagePt.contentWidth;
  const body = geometry.body;
  const blocks: WordBlock[] = [];
  let pendingPageBreak = false;

  const push = (block: WordBlock) => {
    if (pendingPageBreak) {
      pendingPageBreak = false;
      blocks.push({ ...block, pageBreakBefore: true });
      return;
    }
    blocks.push(block);
  };

  const paragraph = (
    runs: WordRun[],
    opts: Partial<Omit<WordParagraph, 'kind' | 'runs'>> & { label: string },
  ): WordParagraph => ({
    kind: 'paragraph',
    runs,
    markLinePt: body.linePt,
    lineRule: 1,
    afterPt: body.spaceAfterPt,
    widthPt: baseWidth,
    justified: body.justified,
    widowControl: true,
    keepNext: false,
    ...opts,
  });

  const shouldSkipBr = (el: Element) => options.editorDom !== false && el.classList.contains('ProseMirror-trailingBreak');

  type InlineState = { bold: boolean; italic: boolean; code: boolean };

  const collectInline = (node: Node, state: InlineState, runs: WordRun[], sizePt: number, fontOverride?: WordFontKind) => {
    if (isText(node)) {
      const text = node.data;
      if (!text) return;
      runs.push({
        text,
        font: fontOverride ?? (state.code ? 'code' : 'narrow'),
        sizePt: fontOverride ? sizePt : state.code ? geometry.inlineCode.sizePt : sizePt,
        bold: state.bold,
        italic: state.italic,
        node,
        nodeOffset: 0,
      });
      return;
    }
    if (!isElement(node)) return;
    if (options.editorDom !== false && isPmArtifact(node)) return;
    const tag = node.tagName;
    if (tag === 'BR') {
      if (shouldSkipBr(node)) return;
      runs.push({ text: '\n', font: fontOverride ?? 'narrow', sizePt, bold: state.bold, italic: state.italic });
      return;
    }
    if (isImageNode(node)) return;
    const next: InlineState = {
      bold: state.bold || tag === 'STRONG' || tag === 'B',
      italic: state.italic || tag === 'EM' || tag === 'I',
      code: state.code || tag === 'CODE',
    };
    node.childNodes.forEach((child) => collectInline(child, next, runs, sizePt, fontOverride));
  };

  const inlineRuns = (el: Element, state: InlineState, sizePt = body.sizePt, fontOverride?: WordFontKind): WordRun[] => {
    const runs: WordRun[] = [];
    el.childNodes.forEach((child) => collectInline(child, state, runs, sizePt, fontOverride));
    return runs;
  };

  const imageBox = (el: Element, label: string): WordBox => ({
    kind: 'box',
    heightPt: readImageHeightPt(el, geometry) + geometry.imageDescentPt,
    afterPt: body.spaceAfterPt,
    splittable: true,
    dom: el,
    label,
  });

  const processParagraphLike = (el: Element, widthPt: number, extra: Partial<WordParagraph> = {}) => {
    const kids = Array.from(el.childNodes);
    if (!kids.some(isImageNode)) {
      push(paragraph(inlineRuns(el, { bold: false, italic: false, code: false }), { widthPt, dom: el, label: 'p', ...extra }));
      return;
    }
    // Text vor/zwischen/nach Bildern → eigene Absätze; jedes Bild eigener Absatz (jc left).
    let buf: Node[] = [];
    const flush = () => {
      // ProseMirror-Hilfsknoten (<br>, Separator-<img>) hinter einer Bild-NodeView sind kein Word-Absatz.
      const meaningful = buf.some(
        (n) => (isText(n) && n.data.trim()) || (isElement(n) && !(options.editorDom !== false && isPmArtifact(n))),
      );
      if (meaningful) {
        const runs: WordRun[] = [];
        buf.forEach((n) => collectInline(n, { bold: false, italic: false, code: false }, runs, body.sizePt));
        push(paragraph(runs, { widthPt, dom: el, label: 'p', ...extra }));
      }
      buf = [];
    };
    kids.forEach((n) => {
      if (isImageNode(n)) {
        flush();
        push(imageBox(n as Element, 'img'));
      } else {
        buf.push(n);
      }
    });
    flush();
  };

  const processList = (list: Element, depth: number) => {
    const indent = geometry.list.indentPt * (depth + 1);
    Array.from(list.children).forEach((li) => {
      if (li.tagName !== 'LI') return;
      const runs: WordRun[] = [];
      const nested: Element[] = [];
      li.childNodes.forEach((child) => {
        if (isElement(child) && (child.tagName === 'UL' || child.tagName === 'OL')) nested.push(child);
        else collectInline(child, { bold: false, italic: false, code: false }, runs, body.sizePt);
      });
      push(paragraph(runs, { widthPt: baseWidth - indent, dom: li, label: 'li' }));
      nested.forEach((n) => processList(n, depth + 1));
    });
  };

  const processPre = (pre: Element) => {
    const codeEl = pre.querySelector('code') ?? pre;
    const runs = inlineRuns(codeEl, { bold: false, italic: false, code: false }, geometry.console.sizePt, 'mono');
    // Jede Textzeile ein Console-Absatz (kein Abstand, 1.15 Zeilen, keine Witwenkontrolle).
    let current: WordRun[] = [];
    const flushLine = () => {
      push(
        paragraph(current, {
          widthPt: baseWidth - geometry.console.indentPt,
          markLinePt: geometry.console.linePt / 1.15,
          lineRule: 1.15,
          afterPt: geometry.console.spaceAfterPt,
          widowControl: geometry.console.widowControl,
          dom: pre,
          label: 'console',
        }),
      );
      current = [];
    };
    for (const run of runs) {
      const parts = run.text.split('\n');
      let consumed = 0;
      parts.forEach((part, idx) => {
        if (idx > 0) flushLine();
        if (part) current.push({ ...run, text: part, nodeOffset: (run.nodeOffset ?? 0) + consumed });
        consumed += part.length + 1;
      });
    }
    flushLine();
  };

  const processTable = (table: Element) => {
    const rows = Array.from(table.querySelectorAll('tr'));
    const cols = rows.reduce((m, tr) => Math.max(m, tr.children.length), 0) || 1;
    const cellWidth = baseWidth / cols - (geometry.finding.metaTable.cellMarginTwips * 2) / 20;
    rows.forEach((tr) => {
      let rowLines = 1;
      let rowLinePt = body.linePt;
      Array.from(tr.children).forEach((cell) => {
        const runs = inlineRuns(cell, { bold: cell.tagName === 'TH', italic: false, code: false });
        const cellPara = paragraph(runs, { widthPt: cellWidth, afterPt: 0, label: 'td' });
        const lines = breakParagraphIntoLines(cellPara, options.measurerForTables ?? fallbackMeasurer, geometry);
        rowLines = Math.max(rowLines, lines.length);
        rowLinePt = Math.max(rowLinePt, ...lines.map((l) => l.heightPt));
      });
      push({
        kind: 'box',
        heightPt: rowLines * rowLinePt + geometry.finding.metaTable.rowBorderPt,
        afterPt: 0,
        splittable: false,
        dom: tr,
        label: 'tr',
      });
    });
  };

  const processBlock = (node: Node, depthWidth = baseWidth) => {
    if (isText(node)) {
      if (node.data.trim()) push(paragraph([bodyRun(node.data.trim(), geometry, { node, nodeOffset: node.data.indexOf(node.data.trim()) })], { label: 'text' }));
      return;
    }
    if (!isElement(node)) return;
    const tag = node.tagName;
    if (isPageBreakElement(node)) {
      pendingPageBreak = true;
      return;
    }
    if (hasPageBreakBeforeAttr(node)) pendingPageBreak = true;
    if (node.hasAttribute('data-word-block')) {
      const h = parseFloat(node.getAttribute('data-word-height-pt') || '0');
      push({ kind: 'box', heightPt: h, afterPt: 0, splittable: true, dom: node, label: node.getAttribute('data-word-block') || 'block' });
      return;
    }
    if (tag === 'P') {
      processParagraphLike(node, depthWidth);
      return;
    }
    if (tag === 'DIV') {
      // Editor-Wrapper (.ProseMirror, EditorContent) sind Container; ein <div> mit reinem
      // Inline-Inhalt wird wie im Python-Konverter als Absatz behandelt.
      const hasBlockChildren = Array.from(node.children).some((c) => BLOCK_TAGS.has(c.tagName));
      if (hasBlockChildren) {
        Array.from(node.childNodes).forEach((child) => processBlock(child, depthWidth));
      } else {
        processParagraphLike(node, depthWidth);
      }
      return;
    }
    if (/^H[1-6]$/.test(tag)) {
      const level = Math.min(3, parseInt(tag.slice(1), 10)) as 1 | 2 | 3;
      const h = geometry.headings[`h${level}` as 'h1' | 'h2' | 'h3'];
      const runs = inlineRuns(node, { bold: true, italic: false, code: false }, h.sizePt);
      push(paragraph(runs, { widthPt: depthWidth, markLinePt: h.linePt, dom: node, label: tag.toLowerCase() }));
      return;
    }
    if (tag === 'UL' || tag === 'OL') {
      processList(node, 0);
      return;
    }
    if (tag === 'BLOCKQUOTE') {
      Array.from(node.childNodes).forEach((child) => {
        if (isElement(child) && child.tagName === 'P') {
          const runs = inlineRuns(child, { bold: false, italic: true, code: false });
          push(paragraph(runs, { widthPt: depthWidth - geometry.blockquoteIndentPt, dom: child, label: 'blockquote' }));
        } else if (isElement(child)) {
          processBlock(child, depthWidth - geometry.blockquoteIndentPt);
        } else if (isText(child) && child.data.trim()) {
          push(paragraph([bodyRun(child.data, geometry, { italic: true, node: child })], { widthPt: depthWidth - geometry.blockquoteIndentPt, label: 'blockquote' }));
        }
      });
      return;
    }
    if (tag === 'PRE') {
      processPre(node);
      return;
    }
    if (tag === 'TABLE') {
      processTable(node);
      return;
    }
    if (isImageNode(node)) {
      push(imageBox(node, 'img'));
      return;
    }
    if (tag === 'HR') {
      // Body-Text-Leerabsatz mit Unterkante (sz 6 = 0.75 pt, space 1 pt).
      push({ kind: 'box', heightPt: body.linePt + 1.75, afterPt: body.spaceAfterPt, splittable: false, dom: node, label: 'hr' });
      return;
    }
    Array.from(node.childNodes).forEach((child) => processBlock(child, depthWidth));
  };

  Array.from(root.childNodes).forEach((child) => processBlock(child));
  return blocks;
}

/** Tabellenzellen brauchen einen Messer schon beim Sammeln; ohne Canvas: 0.456 em Näherung. */
const fallbackMeasurer: TextMeasurer = {
  widthPt: (text, run) => (run.font === 'narrow' ? Array.from(text).length * 0.456 * run.sizePt : monoWidthPt(text, run)),
};

/* ─────────────────────── Finding-Kopf (Template) ─────────────────────── */

export interface WordFindingLeadInput {
  lang?: WordLang;
  /** Erstes Finding im Report trägt die Kapitelzeile „Schwachstellen“. */
  isFirstInReport?: boolean;
  title: string;
  referenzId?: string;
  host?: string;
  domain?: string;
  criticality?: string;
  cvss?: string;
  cwe?: string;
  risks?: string;
  tools?: string;
  commonHtml?: string;
}

export interface WordFindingLeadPart {
  label: string;
  lines: number;
  heightPt: number;
}

export interface WordFindingLeadLayout {
  heightPt: number;
  parts: WordFindingLeadPart[];
}

const dash = (v?: string) => (v && v.trim() ? v.trim() : '\u2013');

export function isBlankRichText(html: string | null | undefined): boolean {
  if (!html) return true;
  const text = html
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/\u2013/g, '')
    .trim();
  return text.length === 0;
}

function parseHtml(html: string): ParentNode | null {
  if (typeof DOMParser === 'undefined') return null;
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  return doc.body;
}

/**
 * Höhe des Finding-Kopfs vor der Detailbeschreibung, wie
 * `fill_schwachstelle_template` ihn setzt: [Kapitel] Titel, Referenz-ID,
 * Metatabelle, Leerzeile, Label, Allgemeine Beschreibung, Label.
 */
export function layoutFindingLead(
  lead: WordFindingLeadInput,
  measurer: TextMeasurer,
  geometry: WordGeometry = wordGeometry(lead.lang ?? 'de'),
): WordFindingLeadLayout {
  const parts: WordFindingLeadPart[] = [];
  const body = geometry.body;
  const f = geometry.finding;
  const labels = f.labels;

  const linesOf = (runs: WordRun[], widthPt: number, extra: Partial<WordParagraph> = {}) =>
    breakParagraphIntoLines(
      {
        kind: 'paragraph',
        runs,
        markLinePt: body.linePt,
        lineRule: 1,
        afterPt: 0,
        widthPt,
        justified: body.justified,
        widowControl: true,
        keepNext: false,
        label: 'lead',
        ...extra,
      },
      measurer,
      geometry,
    );

  if (lead.isFirstInReport) {
    parts.push({ label: labels.chapter, lines: 1, heightPt: f.chapter.linePt });
  }

  const titleLines = linesOf(
    [{ text: dash(lead.title), font: 'narrow', sizePt: f.title.sizePt, bold: true, italic: false }],
    f.title.widthPt,
    { markLinePt: f.title.linePt },
  );
  parts.push({ label: 'Titel', lines: titleLines.length, heightPt: titleLines.length * f.title.linePt });
  parts.push({ label: 'Referenz-ID', lines: 1, heightPt: f.refIdLinePt });

  const cols = f.metaTable.colTwips;
  const margin = (f.metaTable.cellMarginTwips * 2) / 20;
  const colPt = cols.map((c) => c / 20 - margin);
  const spanPt = (cols[1] + cols[2] + cols[3]) / 20 - margin;
  const cellLines = (text: string, widthPt: number, bold = false) =>
    linesOf([bodyRun(text, geometry, { bold })], widthPt).length;
  const rows: number[] = [];
  rows.push(
    Math.max(
      cellLines(labels.metaRows[0], colPt[0]),
      cellLines(dash(lead.host), colPt[1]),
      cellLines(labels.criticality, colPt[2]),
      cellLines(dash(lead.criticality), colPt[3], true),
    ),
  );
  const values = [dash(lead.domain), dash(lead.cvss), dash(lead.cwe), dash(lead.risks), dash(lead.tools)];
  values.forEach((value, i) => {
    rows.push(Math.max(cellLines(labels.metaRows[i + 1], colPt[0]), cellLines(value, spanPt)));
  });
  const tableLines = rows.reduce((a, b) => a + b, 0);
  parts.push({
    label: 'Metadaten-Tabelle',
    lines: tableLines,
    heightPt: tableLines * body.linePt + rows.length * f.metaTable.rowBorderPt,
  });
  parts.push({ label: 'Leerzeile', lines: 1, heightPt: f.gapLinePt });
  parts.push({ label: labels.common, lines: 1, heightPt: f.labelLinePt });

  let commonLines = 0;
  let commonHeight = 0;
  if (isBlankRichText(lead.commonHtml)) {
    commonLines = 1;
    commonHeight = body.linePt + body.spaceAfterPt;
  } else {
    const rootNode = parseHtml(lead.commonHtml || '');
    if (rootNode) {
      const blocks = collectWordBlocks(rootNode, { geometry, editorDom: false, measurerForTables: measurer });
      const lines = layoutWordBlocks(blocks, measurer, geometry);
      commonLines = lines.length;
      commonHeight = flowHeightPt(lines);
    } else {
      commonLines = 1;
      commonHeight = body.linePt + body.spaceAfterPt;
    }
  }
  parts.push({ label: 'Allgemeine Beschreibung', lines: commonLines, heightPt: commonHeight });
  parts.push({ label: labels.description, lines: 1, heightPt: f.labelLinePt });

  const heightPt = parts.reduce((s, p) => s + p.heightPt, 0);
  return { heightPt: Math.round(heightPt * 100) / 100, parts };
}
